/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@i18n/react";
import { useViewedScreen } from "@packages/analytics";
import { useRequestMe, zodRequiredString } from "@packages/sdk";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { spacing } from "../../../../global/emotion";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps, AuthProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "flex-end",
};
const headerStyle: Interpolation = {
  height: spacing.xl,
  width: "100%",
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};

export const AuthNamesFlow = ({
  hideHeader,
  onAuth,
  className,
}: Omit<AuthFlowProps, "onAuth"> & Pick<AuthProps, "onAuth">) => {
  const { query: meQuery } = useRequestMe();
  const t = useTranslations();

  const authFlowNamesDataSchema = z.object({
    lastName: zodRequiredString(`*${t("web_input_error_required")}`),
    name: zodRequiredString(`*${t("web_input_error_required")}`),
  });
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } = useValidator<
    z.infer<typeof authFlowNamesDataSchema>
  >(zodResolver(authFlowNamesDataSchema));
  const form = useForm({ resolver });

  const { mutationPut } = useRequestMe();
  useViewedScreen({ screen: "name_question" });

  const onSubmit = async ({
    lastName,
    name,
  }: z.infer<typeof authFlowNamesDataSchema>) => {
    try {
      await mutationPut.mutateAsync({ last_name: lastName, name });
    } catch (error) {}
    onAuth({ isNewUser: true });
  };

  return (
    <AuthForm<z.infer<typeof authFlowNamesDataSchema>>
      form={form}
      defaultValues={{
        name: meQuery.data?.name || "",
        lastName: meQuery.data?.last_name || "",
      }}
      onSubmit={onSubmit}
      footer={(_, disabled) => (
        <div css={buttonsStyle}>
          <AuthSubmitButton disabled={disabled}>
            {t("onboarding_button_continue")}
          </AuthSubmitButton>
        </div>
      )}
      className={className}
    >
      {(form) => (
        <>
          {!hideHeader && <div css={headerStyle} />}
          <Text type="headline" size="m" as="h2">
            {t("name_input_headline_new")}
          </Text>
          <FormField
            control={form.control}
            name="name"
            showErrorMessage={false}
            defaultValue={""}
            render={({ field }) => (
              <AuthTextInput
                required
                autoFocus
                autoComplete="given-name"
                autoCorrect="off"
                placeholder={t("settings_edit_user_input_hint_name_required")}
                css={fieldsCSS[field.name]}
                {...inputProps}
                {...field}
              />
            )}
            css={inputFormFieldStyle}
          />
          <FormField
            control={form.control}
            name="lastName"
            showErrorMessage={false}
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthTextInput
                  required
                  autoComplete="family-name"
                  autoCorrect="off"
                  placeholder={t(
                    "settings_edit_user_input_hint_last_name_required",
                  )}
                  css={fieldsCSS[field.name]}
                  {...inputProps}
                  {...field}
                />
              </div>
            )}
            css={inputFormFieldStyle}
          />
          <AuthError
            css={[
              errors?.name?.message
                ? errorsCSS.name
                : errors?.lastName?.message
                  ? errorsCSS.lastName
                  : null,
            ]}
          >
            {errors?.name?.message
              ? t(errors.name.message)
              : errors?.lastName?.message
                ? t(errors?.lastName?.message)
                : ""}
          </AuthError>
        </>
      )}
    </AuthForm>
  );
};
